import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft, TrendingUp, Plus, CheckCircle, Clock, AlertTriangle, XCircle,
  BarChart3, Target, Filter, ArrowUpRight, ArrowDownRight, ShieldCheck, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const API = BACKEND_URL;

const STATUS_CONFIG = {
  proposed: { label: 'Proposed', color: '#4338CA', bg: 'bg-indigo-100 text-indigo-800', icon: Zap },
  in_progress: { label: 'In Progress', color: '#B45309', bg: 'bg-amber-100 text-amber-800', icon: Clock },
  completed: { label: 'Completed', color: '#047857', bg: 'bg-green-100 text-green-800', icon: CheckCircle },
  verified: { label: 'Verified', color: '#1D4ED8', bg: 'bg-blue-100 text-blue-800', icon: ShieldCheck },
  cancelled: { label: 'Cancelled', color: '#4B5563', bg: 'bg-gray-100 text-gray-700', icon: XCircle },
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: '#B91C1C', bg: 'bg-red-100 text-red-800' },
  high: { label: 'High', color: '#B45309', bg: 'bg-amber-100 text-amber-800' },
  medium: { label: 'Medium', color: '#1D4ED8', bg: 'bg-blue-100 text-blue-800' },
  low: { label: 'Low', color: '#374151', bg: 'bg-gray-100 text-gray-700' },
};

const CATEGORY_LABELS = { general: 'General', process: 'Process', impact: 'Impact', financial: 'Financial', governance: 'Governance', stakeholder: 'Stakeholder' };
const SOURCE_LABELS = { evaluation: 'Evaluation', feedback: 'Feedback', survey: 'Survey', audit: 'Audit', self_assessment: 'Self-Assessment', '': 'Manual' };
const PIE_COLORS = ['#4338CA', '#B45309', '#047857', '#1D4ED8', '#B91C1C', '#4B5563'];

const ContinuousImprovement = ({ user }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [mainTab, setMainTab] = useState('tracker');
  const [newItem, setNewItem] = useState({
    title: '', description: '', priority: 'medium', category: 'general',
    source_type: '', source_title: '', linked_investment_title: '',
    assigned_to: '', due_date: '', expected_outcome: '',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, [statusFilter, priorityFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/improvements?${params}`, { headers }),
        axios.get(`${API}/api/improvements/stats`, { headers }),
      ]);
      setItems(itemsRes.data || []);
      setStats(statsRes.data);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${API}/api/improvements/seed`, {}, { headers });
      toast.success('Sample data seeded');
      fetchData();
    } catch { toast.error('Seed failed'); }
  };

  const handleCreate = async () => {
    if (!newItem.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/api/improvements`, newItem, { headers });
      toast.success('Improvement created');
      setShowCreate(false);
      setNewItem({ title: '', description: '', priority: 'medium', category: 'general', source_type: '', source_title: '', linked_investment_title: '', assigned_to: '', due_date: '', expected_outcome: '' });
      fetchData();
    } catch { toast.error('Failed to create'); }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await axios.put(`${API}/api/improvements/${itemId}`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchData();
      if (editItem?.id === itemId) {
        const res = await axios.get(`${API}/api/improvements/${itemId}`, { headers });
        setEditItem(res.data);
      }
    } catch { toast.error('Failed to update'); }
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      await axios.put(`${API}/api/improvements/${editItem.id}`, {
        title: editItem.title, description: editItem.description,
        priority: editItem.priority, category: editItem.category,
        assigned_to: editItem.assigned_to, due_date: editItem.due_date,
        expected_outcome: editItem.expected_outcome,
        actual_outcome: editItem.actual_outcome,
        completion_notes: editItem.completion_notes,
      }, { headers });
      toast.success('Saved');
      setEditItem(null);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/api/improvements/${itemId}`, { headers });
      toast.success('Deleted');
      setEditItem(null);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const nextStatuses = (current) => {
    const map = {
      proposed: ['in_progress', 'cancelled'],
      in_progress: ['completed', 'cancelled'],
      completed: ['verified'],
      verified: [],
      cancelled: ['proposed'],
    };
    return map[current] || [];
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  // Chart data
  const statusPieData = stats ? Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label || k, value: v, fill: STATUS_CONFIG[k]?.color || '#888' })) : [];
  const priorityBarData = stats ? Object.entries(stats.by_priority || {}).map(([k, v]) => ({ name: PRIORITY_CONFIG[k]?.label || k, count: v, fill: PRIORITY_CONFIG[k]?.color || '#888' })) : [];
  const sourceBarData = stats ? Object.entries(stats.by_source || {}).map(([k, v]) => ({ name: SOURCE_LABELS[k] || k, count: v })) : [];

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn"><ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" /> Dashboard</Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><TrendingUp className="h-7 w-7 text-indigo-700" aria-hidden="true" /> Continuous Improvement</h1>
            <p className="text-gray-700 mt-1">Track recommendations, monitor implementation, measure results</p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-indigo-700 hover:bg-indigo-800" data-testid="new-improvement-btn"><Plus className="h-4 w-4 mr-2" aria-hidden="true" /> New Action</Button>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="tracker" data-testid="tab-tracker">Tracker</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
        </TabsList>

        {/* ====== TRACKER TAB ====== */}
        <TabsContent value="tracker">
          <h2 className="sr-only">Improvement actions tracker</h2>
          {/* Summary cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Card><CardContent className="p-3 text-center"><span className="block text-2xl font-bold text-gray-800">{stats.total}</span><p className="text-xs text-gray-700">Total Actions</p></CardContent></Card>
              <Card className="bg-amber-50/40 border-amber-100"><CardContent className="p-3 text-center"><span className="block text-2xl font-bold text-amber-700">{stats.by_status?.in_progress || 0}</span><p className="text-xs text-gray-700">In Progress</p></CardContent></Card>
              <Card className="bg-green-50/40 border-green-100"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-700">{stats.completion_rate}%</p><p className="text-xs text-gray-700">Completion Rate</p></CardContent></Card>
              <Card className="bg-blue-50/40 border-blue-100"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{stats.verification_rate}%</p><p className="text-xs text-gray-700">Verified</p></CardContent></Card>
              <Card className={`${stats.overdue > 0 ? 'bg-red-50/40 border-red-100' : ''}`}><CardContent className="p-3 text-center"><span className={`block text-2xl font-bold ${stats.overdue > 0 ? 'text-red-700' : 'text-gray-700'}`}>{stats.overdue}</span><p className="text-xs text-gray-700">Overdue</p></CardContent></Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {['all', 'proposed', 'in_progress', 'completed', 'verified'].map(s => (
              <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'} onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? '' : ''} style={statusFilter === s && s !== 'all' ? { backgroundColor: STATUS_CONFIG[s]?.color } : statusFilter === s ? { backgroundColor: '#1F2937' } : {}}
                data-testid={`filter-status-${s}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
              </Button>
            ))}
            <span className="border-l border-gray-200 mx-1" />
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-28 h-8 text-xs" aria-label="Filter by priority" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Items List */}
          {items.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center justify-center py-16">
              <TrendingUp className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
              <h2 className="text-lg font-semibold text-gray-800 mb-2">No Improvement Actions</h2>
              <p className="text-gray-700 text-center max-w-md mb-4">Create improvement actions from evaluation findings, survey results, and stakeholder feedback.</p>
              <div className="flex gap-3">
                <Button onClick={() => setShowCreate(true)} className="bg-indigo-700 hover:bg-indigo-800"><Plus className="h-4 w-4 mr-2" aria-hidden="true" /> New Action</Button>
                <Button onClick={handleSeed} variant="outline" data-testid="seed-ci-btn">Load Sample Data</Button>
              </div>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {items.map(item => {
                const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.proposed;
                const pc = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
                const StatusIcon = sc.icon;
                const isOverdue = item.due_date && item.due_date < new Date().toISOString() && !['completed', 'verified', 'cancelled'].includes(item.status);
                return (
                  <Card key={item.id} data-testid={`ci-${item.id}`} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setEditItem(item)}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-lg shrink-0" style={{ backgroundColor: sc.color + '15' }}>
                          <StatusIcon className="h-4 w-4" style={{ color: sc.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge className={`text-xs ${sc.bg}`}>{sc.label}</Badge>
                            <Badge className={`text-xs ${pc.bg}`}>{pc.label}</Badge>
                            <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[item.category] || item.category}</Badge>
                            {item.source_type && <Badge variant="outline" className="text-xs">Source: {SOURCE_LABELS[item.source_type] || item.source_type}</Badge>}
                            {isOverdue && <Badge className="text-xs bg-red-100 text-red-700"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" aria-hidden="true" /> Overdue</Badge>}
                          </div>
                          <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                          {item.description && <p className="text-xs text-gray-700 line-clamp-1 mt-0.5">{item.description}</p>}
                          <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-700">
                            {item.linked_investment_title && <span>Investment: {item.linked_investment_title}</span>}
                            {item.assigned_to && <span>Assigned: {item.assigned_to}</span>}
                            {item.due_date && <span>Due: {new Date(item.due_date).toLocaleDateString('en-GB')}</span>}
                          </div>
                        </div>
                        {/* Quick status actions */}
                        <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                          {nextStatuses(item.status).slice(0, 2).map(ns => (
                            <Button key={ns} size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => handleStatusChange(item.id, ns)}
                              style={{ borderColor: STATUS_CONFIG[ns]?.color + '60', color: STATUS_CONFIG[ns]?.color }} data-testid={`action-${item.id}-${ns}`}>
                              {STATUS_CONFIG[ns]?.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ====== DASHBOARD TAB ====== */}
        <TabsContent value="dashboard">
          <h2 className="sr-only">Improvement analytics dashboard</h2>
          {stats && stats.total > 0 ? (
            <div className="space-y-4">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-200"><CardContent className="p-4 text-center"><span className="block text-3xl font-bold text-indigo-700">{stats.total}</span><p className="text-xs text-gray-700">Total Actions</p></CardContent></Card>
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-green-700">{stats.completion_rate}%</p><p className="text-xs text-gray-700">Completion Rate</p></CardContent></Card>
                <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-blue-700">{stats.verification_rate}%</p><p className="text-xs text-gray-700">Verification Rate</p></CardContent></Card>
                <Card className={`bg-gradient-to-br ${stats.overdue > 0 ? 'from-red-50 to-orange-50 border-red-200' : 'from-gray-50 to-slate-50 border-gray-200'}`}><CardContent className="p-4 text-center"><span className={`block text-3xl font-bold ${stats.overdue > 0 ? 'text-red-700' : 'text-gray-700'}`}>{stats.overdue}</span><p className="text-xs text-gray-700">Overdue</p></CardContent></Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Status Distribution Pie */}
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Status Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" label={({ name, value }) => `${name}: ${value}`} fontSize={10}>
                          {statusPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Priority Distribution */}
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Priority</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={priorityBarData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" fontSize={11} />
                        <YAxis allowDecimals={false} fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={36}>
                          {priorityBarData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* By Source */}
                <Card className="lg:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Source</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={sourceBarData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} fontSize={11} />
                        <YAxis dataKey="name" type="category" width={100} fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#4338CA" radius={[0, 4, 4, 0]} barSize={20} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <Card className="border-dashed"><CardContent className="py-12 text-center text-gray-700">No data yet. Create improvement actions to see analytics.</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Improvement Action</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Title *</Label><Input aria-label="Title" value={newItem.title} onChange={e => setNewItem({ ...newItem, title: e.target.value })} placeholder="Improvement action title..." data-testid="ci-title-input" /></div>
            <div><Label>Description</Label><Textarea aria-label="Description" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} rows={2} placeholder="What needs to be improved and why?" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Priority</Label>
                <Select value={newItem.priority} onValueChange={v => setNewItem({ ...newItem, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label>
                <Select value={newItem.category} onValueChange={v => setNewItem({ ...newItem, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Source Type</Label>
                <Select value={newItem.source_type} onValueChange={v => setNewItem({ ...newItem, source_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{Object.entries(SOURCE_LABELS).filter(([k]) => k).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Source Name</Label><Input aria-label="Source Name" value={newItem.source_title} onChange={e => setNewItem({ ...newItem, source_title: e.target.value })} placeholder="e.g., Design Evaluation" /></div>
            </div>
            <div><Label>Linked Investment</Label><Input aria-label="Linked Investment" value={newItem.linked_investment_title} onChange={e => setNewItem({ ...newItem, linked_investment_title: e.target.value })} placeholder="e.g., Digital Inclusion for Rural Elders" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assigned To</Label><Input aria-label="Assigned To" value={newItem.assigned_to} onChange={e => setNewItem({ ...newItem, assigned_to: e.target.value })} placeholder="Username" /></div>
              <div><Label>Due Date</Label><Input aria-label="Due Date" type="date" value={newItem.due_date} onChange={e => setNewItem({ ...newItem, due_date: e.target.value })} /></div>
            </div>
            <div><Label>Expected Outcome</Label><Input aria-label="Expected Outcome" value={newItem.expected_outcome} onChange={e => setNewItem({ ...newItem, expected_outcome: e.target.value })} placeholder="What will improve?" /></div>
            <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} className="bg-indigo-700 hover:bg-indigo-800" data-testid="submit-ci-btn">Create</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Detail Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Improvement Detail</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={STATUS_CONFIG[editItem.status]?.bg}>{STATUS_CONFIG[editItem.status]?.label}</Badge>
                <Badge className={PRIORITY_CONFIG[editItem.priority]?.bg}>{PRIORITY_CONFIG[editItem.priority]?.label}</Badge>
                <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[editItem.category]}</Badge>
              </div>
              {/* Status Actions */}
              {nextStatuses(editItem.status).length > 0 && (
                <div className="flex gap-2">
                  {nextStatuses(editItem.status).map(ns => (
                    <Button key={ns} size="sm" onClick={() => handleStatusChange(editItem.id, ns)}
                      style={{ backgroundColor: STATUS_CONFIG[ns]?.color, color: 'white' }} data-testid={`detail-action-${ns}`}>
                      {STATUS_CONFIG[ns]?.label}
                    </Button>
                  ))}
                </div>
              )}
              <div><Label>Title</Label><Input aria-label="Title" value={editItem.title} onChange={e => setEditItem({ ...editItem, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea aria-label="Description" value={editItem.description || ''} onChange={e => setEditItem({ ...editItem, description: e.target.value })} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Priority</Label>
                  <Select value={editItem.priority} onValueChange={v => setEditItem({ ...editItem, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Category</Label>
                  <Select value={editItem.category} onValueChange={v => setEditItem({ ...editItem, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Assigned To</Label><Input aria-label="Assigned To" value={editItem.assigned_to || ''} onChange={e => setEditItem({ ...editItem, assigned_to: e.target.value })} /></div>
                <div><Label>Due Date</Label><Input aria-label="Due Date" type="date" value={editItem.due_date || ''} onChange={e => setEditItem({ ...editItem, due_date: e.target.value })} /></div>
              </div>
              <div><Label>Expected Outcome</Label><Input aria-label="Expected Outcome" value={editItem.expected_outcome || ''} onChange={e => setEditItem({ ...editItem, expected_outcome: e.target.value })} /></div>
              {['completed', 'verified'].includes(editItem.status) && (
                <>
                  <div><Label>Actual Outcome</Label><Textarea aria-label="Actual Outcome" value={editItem.actual_outcome || ''} onChange={e => setEditItem({ ...editItem, actual_outcome: e.target.value })} rows={2} placeholder="What was actually achieved?" /></div>
                  <div><Label>Completion Notes</Label><Textarea aria-label="Completion Notes" value={editItem.completion_notes || ''} onChange={e => setEditItem({ ...editItem, completion_notes: e.target.value })} rows={2} placeholder="Notes on completion..." /></div>
                </>
              )}
              {/* Source info */}
              {(editItem.source_type || editItem.linked_investment_title) && (
                <Card className="bg-gray-50"><CardContent className="p-3">
                  <p className="text-xs text-gray-700">
                    {editItem.source_type && <span>Source: <strong>{SOURCE_LABELS[editItem.source_type]}</strong> — {editItem.source_title}</span>}
                    {editItem.linked_investment_title && <span className="block mt-0.5">Investment: <strong>{editItem.linked_investment_title}</strong></span>}
                  </p>
                </CardContent></Card>
              )}
              {/* History */}
              {editItem.history && editItem.history.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-700">Status History</Label>
                  <div className="space-y-1 mt-1">
                    {editItem.history.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-700">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_CONFIG[h.status]?.color || '#888' }} />
                        <span className="font-medium" style={{ color: STATUS_CONFIG[h.status]?.color }}>{STATUS_CONFIG[h.status]?.label}</span>
                        <span>by {h.changed_by}</span>
                        <span className="text-gray-700">{new Date(h.changed_at).toLocaleDateString('en-GB')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <Button variant="outline" size="sm" className="text-red-700" onClick={() => handleDelete(editItem.id)}>Delete</Button>
                <div className="flex gap-2"><Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button><Button onClick={handleSaveEdit} className="bg-indigo-700 hover:bg-indigo-800" data-testid="save-ci-btn">Save Changes</Button></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContinuousImprovement;
